<template>
  <div id="app">
    <header v-if="isAuthenticated">    
      <nav class="navbar navbar-expand-lg navbar-dark bg-primary">
        <div class="container">        
          <router-link class="navbar-brand" to="/">{{ $t('navbar.brand') }}</router-link>
          <button class="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">        
            <span class="navbar-toggler-icon"></span>
          </button>
          <div class="collapse navbar-collapse" id="navbarNav">
            <ul class="navbar-nav me-auto">
              <li class="nav-item">    
                <router-link class="nav-link" to="/dashboard">{{ $t('navbar.dashboard') }}</router-link>
              </li>
              <li class="nav-item">    
                <router-link class="nav-link" to="/invoice">{{ $t('navbar.process_invoice') }}</router-link>
              </li>
              <li class="nav-item">
                <router-link class="nav-link" to="/users">{{ $t('navbar.user_management') }}</router-link>
              </li>
              <li class="nav-item">    
                <router-link class="nav-link" to="/credits">{{ $t('navbar.credits') }}</router-link>
              </li>
              <li class="nav-item">    
                <router-link class="nav-link" to="/settings">{{ $t('navbar.settings') }}</router-link>
              </li>
            </ul>
            <div class="navbar-text me-3" v-if="currentUser">
              {{ $t('common.credit_balance', { amount: creditsDisplay }) }}
            </div>
            <div class="navbar-text me-3" v-if="currentUser">
              {{ currentUser.firstName }} {{ currentUser.lastName }}
            </div>
            <button class="btn btn-outline-light me-2" @click="toggleTheme" :title="$t('common.theme_toggle')">
               <span v-if="currentTheme === 'light'">🌙</span>
               <span v-else>☀️</span>
            </button>
            <button class="btn btn-outline-light me-2" @click="toggleLanguage" title="Switch Language">
               {{ $i18n.locale === 'ro' ? 'EN' : 'RO' }}
            </button>
            <button class="btn btn-outline-light" @click="handleLogout">{{ $t('navbar.logout') }}</button>
          </div>
        </div>
      </nav>
    </header>

    <main class="container mt-4">      
      <router-view />
    </main>

    <!-- Credit Limit Warning Modal -->
    <div class="modal fade" id="creditLimitModal" tabindex="-1" ref="creditLimitModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header bg-warning text-dark">
            <h5 class="modal-title">{{ $t('credit_modal.title') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p v-if="companyCredits === 0" v-html="$t('credit_modal.zero_credits')"></p>
            <p v-else v-html="$t('credit_modal.low_credits', { amount: companyCredits })"></p>
            <p class="mb-0">{{ $t('credit_modal.message') }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('credit_modal.close') }}</button>
            <button type="button" class="btn btn-primary" @click="goToCredits">{{ $t('credit_modal.view_options') }}</button>
          </div>
        </div>
      </div>
    </div>

    <footer class="footer mt-5 py-3 bg-body-tertiary">
      <div class="container text-center">
        <a href="/politica-confidentialitate.html" class="text-muted text-decoration-none me-3" target="_blank">{{ $t('common.privacy') }}</a>
        <a href="/termeni-si-conditii.html" class="text-muted text-decoration-none me-3" target="_blank">{{ $t('common.terms') }}</a>
        <span class="text-muted">{{ $t('common.footer', { year: new Date().getFullYear() }) }}</span>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { Modal } from 'bootstrap' // Ensure bootstrap imports work in this env, or use window.bootstrap if loaded via CDN, but package.json likely has it

export default {
  name: 'App',
  data() {
    return {
      limitModal: null
    }
  },
  computed: {
    ...mapGetters('auth', [
      'isAuthenticated',
      'isAdmin',
      'currentUser',
      'creditsDisplay',
      'companyCredits' // Needed for logic
    ]),
    ...mapGetters('settings', [
      'darkMode',
      'getSetting'
    ]),
    currentTheme() {
      return this.darkMode ? 'dark' : 'light';
    }
  },
  watch: {
    '$i18n.locale': {
      immediate: true,
      handler(newLocale) {
        document.title = newLocale === 'ro' ? 'Factura2Vama' : 'Invoice2Customs';
      }
    },
    isAuthenticated: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchSettings();
        }
      }
    },
    // Watch for credit changes (processing or login)
    companyCredits: {
        handler(newVal) {
            this.checkCreditLimit(newVal);
        }
    },
    // Watch for user changes (login) - credits might not change number but load
    currentUser: {
        handler(newVal) {
            if (newVal) {
                // Next tick to ensure getter is ready
                this.$nextTick(() => {
                    this.checkCreditLimit(this.companyCredits);
                });
            }
        }
    }
  },
  mounted() {
      // Initialize modal
      // We need to wait for DOM. 
      // Safest to init on demand or check existence.
      // But we can init once mounted if element exists (it should always be there)
      this.initModal();
  },
  methods: {
    ...mapActions('auth', ['logout']), 
    ...mapActions('settings', ['fetchSettings', 'updateSetting']),
    
    initModal() {
        const el = this.$refs.creditLimitModal;
        if (el) {
            this.limitModal = new Modal(el);
        }
    },
    
    checkCreditLimit(credits) {
        if (!this.isAuthenticated || !this.currentUser) return;
        if (this.currentUser.subscription_type === 'UNLIMITED') return;
        
        // Show if 1 or 0
        if (credits !== undefined && credits <= 1) {
            // Re-init if missing (e.g. fast switching)
            if (!this.limitModal) this.initModal();
            if (this.limitModal) this.limitModal.show();
        }
    },
    
    goToCredits() {
        if (this.limitModal) this.limitModal.hide();
        this.$router.push('/credits');
    },

    async handleLogout() { 
      try { 
        await this.logout()
        window.location.href = '/' 
      } catch (error) { 
        console.error('Logout error:', error)
        window.location.href = '/' 
      } 
    },
    toggleTheme() {
      const newMode = this.darkMode ? 'Light' : 'Dark';
      this.updateSetting({ id: 'mode', value: newMode });
    },
    toggleLanguage() {
      const newLang = this.$i18n.locale === 'ro' ? 'en' : 'ro';
      this.updateSetting({ id: 'language', value: newLang });
    }
  }
}
</script>

<style>
/* Import Bootstrap CSS */
@import 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css';

#app {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

main {
  flex: 1;
}

.footer {
  margin-top: auto;
}
</style>